import { API_BASE_URL } from '@/utils/config';
import type { GlossLevel } from '@/types';

export interface UploadResponse {
  imageId: string;
  uploadUrl: string;
  publicUrl: string;
  expiresAt: number;
}

export interface PreviewResponse {
  jobId: string;
  status: string;
  checkUrl: string;
  estimatedSeconds: number;
  strategy: string;
}

export interface PreviewStatusResponse {
  jobId: string;
  status: 'queued' | 'submitted_to_bria' | 'bria_processing' | 'completed' | 'error';
  progress: number;
  resultUrl?: string;
  metadata?: {
    finishId: string;
    glossLevel: GlossLevel;
    strategy: string;
    includeBacksplash?: boolean;
  };
  error?: string;
}

export interface MaskResponse {
  maskId: string;
  maskUrl: string;
  format: string;
  note: string;
}

export interface ManualMaskResponse {
  maskId: string;
  maskUrl: string;
}

const apiUrl = (path: string) =>
  API_BASE_URL ? `${API_BASE_URL.replace(/\/+$/, '')}${path}` : path;

export async function requestUploadUrl(
  fileName: string,
  contentType: string,
  fileSize: number,
  signal?: AbortSignal
): Promise<UploadResponse> {
  const res = await fetch(apiUrl('/api/v2/upload'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName, contentType, fileSize }),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || `Upload error: ${res.status}`);
  }

  return res.json();
}

export async function uploadToPresignedUrl(
  presignedUrl: string,
  file: File,
  signal?: AbortSignal
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
      signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    // Network or CORS failure — browser blocks cross-origin PUTs that have no CORS headers
    throw new Error(
      'Upload blocked: the storage bucket is not accepting uploads from this origin. ' +
      'Please try again or contact support.'
    );
  }

  if (!res.ok) {
    throw new Error(`Upload failed (${res.status}): storage rejected the file. Please try again.`);
  }
}

export async function submitPreviewJob(
  params: {
    imageId: string;
    imageUrl?: string;
    finishId: string;
    glossLevel: GlossLevel;
    includeBacksplash?: boolean;
    strategy?: string;
    sessionId?: string;
    deviceTier?: string;
    tenantSlug?: string;
  },
  signal?: AbortSignal
): Promise<PreviewResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (params.tenantSlug && params.tenantSlug !== 'default') {
    headers['X-Tenant-Slug'] = params.tenantSlug;
  }

  const res = await fetch(apiUrl('/api/v2/preview'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      imageId: params.imageId,
      imageUrl: params.imageUrl,
      finishId: params.finishId,
      glossLevel: params.glossLevel,
      includeBacksplash: params.includeBacksplash,
      strategy: params.strategy,
      sessionId: params.sessionId,
      deviceTier: params.deviceTier,
    }),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Preview submission failed' }));
    throw new Error(err.error || `Preview error: ${res.status}`);
  }

  return res.json();
}

export async function checkPreviewStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<PreviewStatusResponse> {
  const res = await fetch(apiUrl(`/api/v2/preview/status/${jobId}?_cb=${Date.now()}`), {
    cache: 'no-store',
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Status check failed' }));
    throw new Error(err.error || `Status error: ${res.status}`);
  }

  return res.json();
}

export async function generateMask(
  params: {
    imageId?: string;
    imageUrl?: string;
  },
  signal?: AbortSignal
): Promise<MaskResponse> {
  const res = await fetch(apiUrl('/api/v2/mask'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Mask generation failed' }));
    throw new Error(err.error || `Mask error: ${res.status}`);
  }

  return res.json();
}

export async function uploadManualMask(
  params: {
    mask: string; // base64 data URI
    originalImageId?: string;
  },
  signal?: AbortSignal
): Promise<ManualMaskResponse> {
  const res = await fetch(apiUrl('/api/v2/mask/manual'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Mask upload failed' }));
    throw new Error(err.error || `Mask upload error: ${res.status}`);
  }

  return res.json();
}
